import React, { useRef, useEffect, useState } from 'react'
import { TextField } from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'

export const filter = createFilterOptions<Record<string, any>>()

export interface AutoCompleteProps {
  name: string
  label: string
  optionList: Array<Record<string, any>>
  placeholder?: string
  required?: boolean
  onChange?: (value: any) => void
  value?: Record<string, any> | null
  extraProps?: Record<string, any>
  defaultValue?: string | undefined
}

const AutoCompleteComponent: React.FC<AutoCompleteProps> = ({
  name,
  label,
  optionList = [],
  placeholder = 'ALL',
  required = false,
  onChange,
  value = null,
  extraProps = {},
  defaultValue = null
}) => {
  const autoCompleteRef = useRef<HTMLInputElement>(null)
  const [internalValue, setInternalValue] = useState<
    Record<string, any> | null | undefined
  >(value)

  // Observe the change of external value
  useEffect(() => {
    setInternalValue(value)
  }, [value])

  // Observe the change of optionList
  useEffect(() => {
    if (!internalValue) return

    // Check if the current value exists in the new option list
    const isValueValid = optionList.some(
      option => option.id === internalValue.id
    )

    // If the value is invalid, clear the current value
    if (!isValueValid) {
      setInternalValue(null)
      if (autoCompleteRef.current) {
        autoCompleteRef.current.value = ''
      }
      onChange?.(null)
    }
  }, [optionList, name, onChange, internalValue])

  useEffect(() => {
    if (defaultValue) {
      const defaultOption = optionList.find(
        option => option.id === defaultValue
      )
      setInternalValue(defaultOption)
      if (defaultOption) {
        if (autoCompleteRef.current) {
          autoCompleteRef.current.value = defaultOption.id
        }
        onChange?.(defaultOption)
      }
    }
  }, [defaultValue])

  return (
    <Autocomplete
      options={optionList}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={option => option.name || ''}
      filterOptions={(options, params) => {
        const filtered = filter(options, params) as Record<string, any>[]
        return filtered
      }}
      onChange={(_, newValue) => {
        setInternalValue(newValue)
        if (autoCompleteRef.current) {
          const value = newValue ? newValue.id : ''
          autoCompleteRef.current.value = value
          onChange?.(newValue)
        }
      }}
      value={internalValue}
      renderInput={params => {
        return (
          <>
            <TextField
              {...params}
              key={name}
              label={label}
              variant='outlined'
              placeholder={placeholder}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '36px'
                }
              }}
              {...(required ? { required: true } : {})}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <input type='hidden' name={name} ref={autoCompleteRef} />
          </>
        )
      }}
      limitTags={1}
      size='small'
      sx={{
        width: '100%',
        height: '36px'
      }}
      {...extraProps}
      slotProps={{
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': {
              whiteSpace: 'normal',
              wordBreak: 'break-all'
            }
          }
        }
      }}
    />
  )
}

export default AutoCompleteComponent
